import { useMemo } from 'react'
import { type MRT_ColumnDef } from 'material-react-table'
import { useParams } from 'react-router-dom'
import { useAppSelector } from '../../../../app/hooks'
import { useGetShipmentByIdQuery } from '../../../../app/redux-fetch/apiOrders'
import { formatDate } from '../../../../app/utils'
import MRTDataGridv2 from '../../../../components/MRTDataGrid/MRTDataGridv2'
import { TableTitleTypography } from '../../../../components/Typographies/TableTitleTypography'
import { fetchLoggedInUserSelector } from '../../../selectors'
import { ILoggedInUser } from '../../../types'

interface IShipmentInfoRow {
  status: string
  customerName: string
  customerReferenceNumber: string
  description: string
  lastStatusChangeDate: string
}

const ViewShipmentTabHeader = () => {
  const { id } = useParams()
  const loggedInUser = useAppSelector(fetchLoggedInUserSelector.data) || ({} as ILoggedInUser)
  const tenantId = loggedInUser?.tenantId
  const isTenantUser = loggedInUser?.tenantId && !loggedInUser?.customerId

  const {
    data: shipment,
    isFetching: shipmentFetching,
  } = useGetShipmentByIdQuery({
    shipmentId: Number(id),
    tenantId,
  })

  const columns = useMemo<Array<MRT_ColumnDef<IShipmentInfoRow>>>(() => {
    const cols: Array<MRT_ColumnDef<IShipmentInfoRow>> = [
      {
        accessorKey: 'status',
        header: 'Status',
      },
    ]

    if (isTenantUser) {
      cols.push({
        accessorKey: 'customerName',
        header: 'Customer',
      })
    }

    cols.push(
      {
        accessorKey: 'customerReferenceNumber',
        header: 'Customer Reference Number',
      },
      {
        accessorKey: 'description',
        header: 'Cargo Description',
      },
      {
        accessorKey: 'lastStatusChangeDate',
        header: 'Last Status Change',
        Cell: ({ cell }) => {
          const value = cell.getValue<string>()
          return value ? formatDate(value) : ''
        },
      }
    )

    return cols
  }, [isTenantUser])

  const data = useMemo<IShipmentInfoRow[]>(() => {
    if (!shipment) return []
    return [
      {
        status: shipment.status ?? '',
        customerName: shipment.customer?.name ?? '',
        customerReferenceNumber: shipment.customerReferenceNumber ?? '',
        description: shipment.description ?? '',
        lastStatusChangeDate: shipment.lastStatusChangeDate ?? '',
      },
    ]
  }, [shipment])

  return (
    <MRTDataGridv2
      columns={columns}
      data={data}
      leftHeadingComponent={
        <TableTitleTypography>Shipment Information</TableTitleTypography>
      }
      enableTopToolbar={false}
      enableBottomToolbar={false}
      enableColumnActions={false}
      enableColumnFilters={false}
      enablePagination={false}
      enableSorting={false}
      enableGlobalFilter={false}
      state={{
        isLoading: shipmentFetching,
      }}
    />
  )
}

export default ViewShipmentTabHeader
